import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import uniqueId from 'lodash/uniqueId';

export function isObject(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null;
}

export function cloneObject<T>(obj: T): T {
  return cloneDeep(obj);
}

export function isObjectEqual<T>(a: T, b: T): boolean {
  return isEqual(a, b);
}

export function getUniqueString(name?: string): string {
  return uniqueId(name + '__');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function nullsToUndefined(obj: any): any {
  if (obj === null || obj === undefined) {
    return undefined;
  }

  if (obj.constructor.name === 'Object' || obj.constructor.name === 'Array') {
    for (const key in obj) {
      obj[key] = nullsToUndefined(obj[key]);
    }
  }

  return obj;
}

interface IEnumObject {
  [key: string]: string | number;
}

export function enumParseValue<T extends IEnumObject>(enumObj: T, value: string | number): T[keyof T] | undefined {
  const lowerValue = value.toString().toLowerCase();
  const enumValues = Object.values(enumObj).map((v) => v.toString().toLowerCase());

  const index = enumValues.indexOf(lowerValue);
  if (index !== -1) {
    return enumObj[Object.keys(enumObj)[index] as keyof T];
  } else {
    return undefined;
  }
}

export function enumCount<T extends IEnumObject>(enumObj: T): number {
  return Object.values(enumObj).filter((v) => (v as number) >= 0).length;
}

export const enumKeyAtIndex = <T extends IEnumObject>(enumObj: T, idx: number | number): string => {
  const keys = Object.keys(enumObj);
  if (idx < 0 || idx >= keys.length) {
    throw new Error(`Index ${idx} is out of bounds for enum with ${keys.length} values.`);
  }
  const key = keys[idx];
  if (!key) {
    throw new Error(`No key found at index ${idx} in enum.`);
  }

  return enumObj[key] as string;
};

export function enumParseKey<T extends IEnumObject>(enumObj: T, key: string): T[keyof T] | undefined {
  const lowerKey = key.toLowerCase();
  const enumKeys = Object.keys(enumObj).map((v) => v.toLowerCase());

  const index = enumKeys.indexOf(lowerKey);
  if (index !== -1) {
    return enumObj[Object.keys(enumObj)[index] as keyof T];
  } else {
    return undefined;
  }
}

/**
 * Deep-clones `input` while discarding all keys that contain "@odata."
 *
 * @param input      Any JSON-serialisable value (object, array, primitive)
 * @param keepType   When true, preserves keys that are *exactly* "@odata.type"
 *                   (useful for Graph payloads that require the type field).
 *                   Default = false (strip everything).
 *
 * @example
 *   const cleanLayout = stripOData(exampleLayout, /* keepType * / true);
 *   await graphClient.api(pageUrl).patch({ canvasLayout: cleanLayout });
 */
export function stripOData<T>(input: T, keepType: boolean = false): T {
  const seen = new WeakMap<object, unknown>();

  const walk = <U>(value: U): U => {
    // 1) Primitives
    if (value === null || typeof value !== 'object') {
      return value;
    }

    // 2) Cycle / shared reference protection
    const cached = seen.get(value as object);
    if (cached !== undefined) {
      return cached as U;
    }

    // 3) Arrays
    if (Array.isArray(value)) {
      const arr: unknown[] = [];
      seen.set(value, arr);

      for (const item of value) {
        arr.push(walk(item));
      }

      return arr as U;
    }

    // 4) Objects
    const obj: Record<string, unknown> = {};
    seen.set(value, obj);

    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (key.includes('@odata.') && !(keepType && key === '@odata.type')) {
        continue;
      }
      obj[key] = walk(v);
    }

    return obj as U;
  };

  return walk(input);
}
